import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import BomTree from './tree/BomTree';
import { ToastProvider } from './ui/Toast';
import { loadSettings, setBomUser, bomViewsFor } from './settings/settingsStore';
import './bom-design-system.css';
import './BomViewer.css';

// The views, in nav order — rendered as sub-items under BOM in the platform's left nav.
// The key is the URL segment (`/bom/:view`) and what the server's /api/bom/roots `view`
// maps to a filter id. These two are always offered; DMS items and the ECO list join them
// once their filters are set (`bomViewsFor` in settingsStore).
export const BOM_VIEWS = bomViewsFor(null);

// Every view this build knows, with the setting it needs and what its rows are called.
const VIEW_META = {
  // Under the assembly structure the BOMs view reads `assemblyRootFilterId` instead — the
  // filter a view needs depends on the structure, so `filterKeyFor` resolves it.
  boms: { filterKey: 'bomFilterId', noun: 'BOMs' },
  // The assembly work item as its own top-level list, offered only under that structure.
  // Its rows expand exactly like a BOM's: sub-assemblies, then parts.
  assemblies: { filterKey: 'assemblyFilterId', noun: 'Assemblies', missing: 'Sub-assembly' },
  parts: { filterKey: 'partCatalogFilterId', noun: 'Parts', missing: 'Part Catalog' },
  dms: { filterKey: 'dmsFilterId', noun: 'DMS items', missing: 'DMS items' },
  ecos: { filterKey: 'ecoFilterId', noun: 'ECOs', missing: 'ECO' },
};

/**
 * BOM module — the standalone Covaris BOM Viewer's App, re-hosted as a platform module.
 *
 * What was removed on the way in, and where it went:
 *   * its LOGIN and idle sign-out  → the platform session (one sign-in, one expiry clock)
 *   * its SIDEBAR (views + user menu) → the platform left nav (views as sub-items) + header
 *   * `settings.xml`               → a per-tenant row, loaded here before the tree mounts and
 *                                    edited in Admin → Settings (user menu)
 *
 * Everything below the header is wrapped in `.bom-scope`, which every BOM stylesheet is
 * prefixed with (see bom-design-system.css). Nothing BOM renders is styled without it.
 */
// Memoised: App re-renders every second for its session countdown, and nothing in that
// render concerns BOM. Without this, every row of an expanded tree re-rendered each second.
export default React.memo(function BomViewer({ userId, onSessionExpired }) {
  return (
    <ToastProvider>
      <BomViewerInner userId={userId} onSessionExpired={onSessionExpired} />
    </ToastProvider>
  );
});

function BomViewerInner({ userId, onSessionExpired }) {
  const { view: viewParam } = useParams();
  const view = VIEW_META[viewParam] ? viewParam : null;

  // 'loading' | 'ready' | 'unconfigured' | 'error'
  const [status, setStatus] = useState('loading');
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  // Where-Used target: { sqlArg, cs21Int, label } while active, null otherwise.
  const [whereUsedTarget, setWhereUsedTarget] = useState(null);

  // Set synchronously, before the tree's first render reads it for its preference keys.
  setBomUser(userId);

  // ⚠️ App re-renders EVERY SECOND (the session countdown) and passes a fresh inline
  // `onSessionExpired` each time. Anything that depends on that identity re-runs every
  // second — it sat in `loadTop`'s dependencies, so the whole grid re-fetched from Orcanos
  // once a second: rows flashed, expanded branches collapsed, the backend drowned in
  // requests. Read it through a ref so every callback below is stable.
  const sessionExpiredRef = useRef(onSessionExpired);
  sessionExpiredRef.current = onSessionExpired;

  const applyPayload = useCallback((payload) => {
    setInfo(payload);
    setStatus(payload.configured ? 'ready' : 'unconfigured');
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadSettings()
      .then((payload) => { if (!cancelled) applyPayload(payload); })
      .catch((err) => {
        if (cancelled) return;
        if (err.httpCode === 401) { sessionExpiredRef.current?.(); return; }
        setError(err.message);
        setStatus('error');
      });
    return () => { cancelled = true; };
  }, [applyPayload]);

  // Leaving a view also leaves Where-Used — its results would otherwise sit under the
  // wrong view's label.
  useEffect(() => { setWhereUsedTarget(null); }, [view]);

  const handleWhereUsed = useCallback((node) => {
    // Two values per target:
    //   sqlArg  — the id the server passes to dbo.fn_GetRootParentByCS21:
    //             PRT → the part's own id; PI → the CS21 integer from Master Part Source.
    //   cs21Int — what Locate matches descendants on. PRT.itemId and a PI's CS21 integer
    //             both name the same source part.
    const isPart = node.row.type === 'PRT';
    const sqlArg = isPart
      ? String(node.row.itemId || '').trim()
      : String(node.row.cs21Int || '').trim();
    const cs21Int = isPart ? String(node.row.itemId || '').trim() : node.row.cs21Int || '';
    setWhereUsedTarget({ sqlArg, cs21Int, label: node.row.objName || `#${sqlArg}` });
  }, []);

  const handleExitWhereUsed = useCallback(() => setWhereUsedTarget(null), []);

  const handleAuthExpired = useCallback(() => { sessionExpiredRef.current?.(); }, []);

  if (!view) return <Navigate to="/bom/boms" replace />;

  const meta = VIEW_META[view];
  const listed = bomViewsFor(info?.settings).find((v) => v.key === view);
  const viewLabel = listed?.label || meta.missing || meta.noun;
  const canEdit = !!info?.can_edit;
  // A list view whose filter isn't set renders its own state instead of the tree (a bookmark
  // to /bom/ecos outlives the admin clearing the ECO filter).
  // Under the assembly structure the BOMs view reads the assembly ROOT filter, not the PRT
  // BOM filter — a tenant on assemblies may never own the latter.
  const filterKey = (view === 'boms' && info?.settings?.useAssembly)
    ? 'assemblyRootFilterId' : meta.filterKey;
  const partsMissing = status === 'ready' && !!meta.missing && !info.settings[filterKey];

  return (
    <div className="bom-scope bom-module">
      <div className="bom-module-head">
        <div className="bom-module-title">
          <h2>{viewLabel}</h2>
        </div>
      </div>

      {status === 'loading' && <div className="bom-module-state">Loading…</div>}

      {status === 'error' && (
        <div className="bom-module-state bom-module-state--error" role="alert">{error}</div>
      )}

      {status === 'unconfigured' && (
        <div className="bom-module-state">
          <h3>The BOM viewer isn't set up for this account yet</h3>
          <p>
            It reads saved Orcanos filters — top-level BOMs, part instances, the part catalogue
            and related ECOs — and needs their ids before it can show anything.
          </p>
          {canEdit ? (
            <Link to="/settings" className="btn-primary">Set up in Admin → Settings</Link>
          ) : (
            <p className="bom-module-muted">Ask an Orcanos administrator to configure it.</p>
          )}
        </div>
      )}

      {partsMissing && (
        <div className="bom-module-state">
          <h3>No {meta.missing} filter is configured</h3>
          <p className="bom-module-muted">
            {canEdit
              ? <>Add its filter id in <Link to="/settings">Admin → Settings</Link>.</>
              : 'Ask an Orcanos administrator to add it.'}
          </p>
        </div>
      )}

      {status === 'ready' && !partsMissing && (
        <div className="bom-module-body">
          {/* The main tree stays MOUNTED while Where-Used is open — hidden, not unmounted — so
              its page, search, expanded rows and child cache survive the round trip. */}
          <div
            className="bom-module-pane"
            style={{ display: whereUsedTarget ? 'none' : 'contents' }}
          >
            <BomTree
              key={`main/${view}`}
              onAuthExpired={handleAuthExpired}
              view={view}
              topLabel={listed?.label || meta.noun}
              onWhereUsed={handleWhereUsed}
            />
          </div>
          {whereUsedTarget && (
            <BomTree
              key={`wu/${whereUsedTarget.sqlArg}`}
              onAuthExpired={handleAuthExpired}
              view="boms"
              topLabel="BOMs"
              topWhereUsedId={whereUsedTarget.sqlArg}
              targetOriginalId={whereUsedTarget.sqlArg}
              targetCs21Int={whereUsedTarget.cs21Int}
              whereUsedLabel={whereUsedTarget.label}
              onExitWhereUsed={handleExitWhereUsed}
              onWhereUsed={handleWhereUsed}
            />
          )}
        </div>
      )}

    </div>
  );
}
